/**
 * Mappers lignes Supabase → forme Zustand / UI.
 * Les colonnes SQL portent l’index ; `payload` (JSONB) porte le détail app (round-trip avec *AppToDbRow).
 */

function timeFromDbTime(t) {
  if (t == null) return "09:00";
  const s = String(t);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "09:00";
  return `${String(m[1]).padStart(2, "0")}:${m[2]}`;
}

/**
 * @param {object} row — ligne `clients`
 * @returns {object|null}
 */
export function clientDbToApp(row) {
  if (!row || row.id == null) return null;
  const pl = row.payload && typeof row.payload === "object" && Object.keys(row.payload).length > 0 ? row.payload : null;
  if (pl) {
    return {
      ...pl,
      id: row.id,
      constraints: row.constraints ?? pl.constraints,
      disponibilites: row.disponibilites ?? pl.disponibilites,
      preferences: row.preferences ?? pl.preferences,
      aiSummary: row.ai_summary ?? pl.aiSummary,
      aiSummaryDate: row.ai_summary_date ?? pl.aiSummaryDate,
    };
  }

  const fn = row.first_name || "";
  const ln = row.last_name || "";
  const name = [fn, ln].filter(Boolean).join(" ").trim() || "Client";
  let notes = [];
  if (typeof row.notes === "string" && row.notes.trim()) {
    notes = [{ id: 1, date: "", text: row.notes }];
  }

  return {
    id: row.id,
    name,
    firstName: fn,
    lastName: ln,
    email: row.email || undefined,
    phone: row.phone || undefined,
    birthDate: row.birth_date || undefined,
    gender: row.gender || undefined,
    objective: row.objective || undefined,
    constraints: row.constraints || undefined,
    disponibilites: row.disponibilites || undefined,
    preferences: row.preferences || undefined,
    aiSummary: row.ai_summary || undefined,
    aiSummaryDate: row.ai_summary_date || undefined,
    notes,
    status: row.status === "archived" || row.status === "standby" ? row.status : "active",
    billingMode: ["session", "pack", "subscription", "programme"].includes(row.billing_mode) ? row.billing_mode : "session",
    previousStatus: row.previous_status || undefined,
    statusChangedAt: row.status_changed_at || undefined,
    tags: [],
    sessions: [],
    cancelCount: 0,
  };
}

/**
 * @param {object} row — ligne `events`
 * @returns {object|null}
 */
export function eventDbToApp(row) {
  if (!row || row.id == null) return null;
  const pl = row.payload && typeof row.payload === "object" && Object.keys(row.payload).length > 0 ? row.payload : null;
  if (pl) {
    return { ...pl, id: row.id };
  }

  const recurringGroupId = row.recurrence_group_id || undefined;
  return {
    id: row.id,
    coachId: row.coach_id,
    clientId: row.client_id || undefined,
    client: "",
    date: row.date,
    time: timeFromDbTime(row.start_time),
    duration: row.duration ?? 60,
    status: row.status || "scheduled",
    type: recurringGroupId ? "recurring" : "one_shot",
    recurringGroupId,
    sessionTitle: row.title || null,
    billable: row.billable !== false,
    billingMode: row.billing_mode || undefined,
    clientProductId: row.client_product_id || undefined,
    workoutId: row.workout_id || null,
    sessionNotes: row.session_notes || undefined,
    aiSummary: row.ai_summary || undefined,
    location: row.location || undefined,
    tarif: undefined,
  };
}

/**
 * @param {object} row — ligne `products`
 * @returns {object|null}
 */
export function productDbToApp(row) {
  if (!row || row.id == null) return null;
  const pl = row.payload && typeof row.payload === "object" && Object.keys(row.payload).length > 0 ? row.payload : null;
  if (pl) {
    return { ...pl, id: row.id };
  }

  let type = row.type === "program" ? "programme" : row.type;
  if (!["session", "pack", "subscription", "programme"].includes(type)) return null;

  let pricing = { amount: 0, perSession: null, recurrence: null };
  let sessions = {};
  let subscription;
  let durationWeeks;
  let programmeObjective;
  let includesAssessment;
  let paymentSchedule;

  if (type === "session") {
    pricing = { amount: Number(row.unit_price) || 0, perSession: null, recurrence: null };
  } else if (type === "pack") {
    const cnt = Math.max(1, Number(row.pack_size) || 10);
    const per = Number(row.pack_unit_price) || 0;
    pricing = { amount: Math.round(per * cnt * 100) / 100, perSession: per, recurrence: null };
    sessions = { count: cnt, packValidityStart: "first_session" };
  } else if (type === "subscription") {
    const cap = Math.max(1, Number(row.subscription_sessions) || 4);
    pricing = { amount: Number(row.subscription_price) || 0, perSession: null, recurrence: "monthly" };
    sessions = { count: cap };
    subscription = { maintainByDefaultOnStandby: false, standbyBillingMode: "full_month" };
  } else if (type === "programme") {
    const cnt = Math.max(1, Number(row.pack_size) || 8);
    const per = Number(row.pack_unit_price) || 0;
    const weeks = row.duration_weeks != null ? Number(row.duration_weeks) : 12;
    pricing = { amount: Math.round(per * cnt * 100) / 100, perSession: per, recurrence: null };
    sessions = { count: cnt, validityDays: weeks * 7 };
    durationWeeks = weeks;
    programmeObjective = row.objective || undefined;
    includesAssessment = !!row.includes_assessment;
    paymentSchedule = row.payment_schedule || "once";
  }

  return {
    id: row.id,
    name: row.name || "Offre",
    type,
    pricing,
    sessions,
    subscription,
    programmeObjective,
    durationWeeks,
    includesAssessment,
    paymentSchedule,
    isActive: row.is_active !== false,
    createdAt: row.created_at ? String(row.created_at).slice(0, 10) : undefined,
    description: "",
    deliveryMode: "in_person",
  };
}
